import { TemplateGalleryCard } from '@common/types';
import { useProfilePageBi } from '@components/Hooks/useProfilePageBi';
import LoadingTemplateGallery from '@components/Profile/ProfileTemplates/LoadingTemplateGallery';
import {
  MAX_TEMPLATES_COUNT_FOR_THREE_IN_ROW,
  PROFILE_INITIAL_TEMPLATE_COUNT,
  PROFILE_INITIAL_TEMPLATE_COUNT_MOBILE,
} from '@utils/profileTemplatesUtils';
import {
  calculateNextCount,
  getNewTemplateSliceIndices,
} from '@utils/profileTemplatesUtils';
import { Box } from '@wix/design-system';
import { useTranslation } from '@wix/fe-essentials-standalone/i18n';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import CollapseButton from '../CollapseButton';
import RenderGallery from './RenderGallery';
import ArrowSvg from '@components/Images/ArrowSvg';
import s from './ProfileTemplates.module.scss';
interface TemplatesProps {
  isMobileView: boolean;
  templatesRef: React.RefObject<HTMLDivElement>;
  templates: TemplateGalleryCard[];
  onNeedMoreTemplates: () => void;
  totalAvailable?: number;
  onVisibleCountChange: (count: number) => void;
  onShowLess?: () => void;
}

const FOCUSABLE_SELECTOR = 'a, button, [tabindex]:not([tabindex="-1"])';

const ProfileTemplateGalleries: React.FC<TemplatesProps> = ({
  isMobileView,
  templatesRef,
  templates,
  onNeedMoreTemplates,
  totalAvailable = 0,
  onVisibleCountChange,
  onShowLess,
}) => {
  const initialTemplateCount = useMemo(() => {
    return isMobileView
      ? PROFILE_INITIAL_TEMPLATE_COUNT_MOBILE
      : PROFILE_INITIAL_TEMPLATE_COUNT;
  }, [isMobileView]);
  const [visibleCount, setVisibleCount] = useState(initialTemplateCount);
  const [renderedGalleries, setRenderedGalleries] = useState<React.ReactNode[]>(
    [],
  );

  useEffect(() => {
    onVisibleCountChange(visibleCount);
  }, [visibleCount]);

  const { t } = useTranslation();
  const { logTemplateClick } = useProfilePageBi();

  const focusFirstTemplateCard = (root?: Element | null) => {
    const first = root?.querySelector(FOCUSABLE_SELECTOR) as HTMLElement | null;
    first?.focus({ preventScroll: true });
  };

  const replaceLoadingGalleryWithContent = useCallback(
    (galleries: React.ReactNode[]): React.ReactNode[] => {
      if (galleries.length === 0) return galleries;

      const lastGallery = galleries.at(-1);
      const isLoadingGallery =
        React.isValidElement(lastGallery) &&
        lastGallery.key === 'loading-gallery';
      if (!isLoadingGallery) {
        return galleries;
      }

      const loadingGalleryIndex = galleries.length - 1;
      const { startIndex, endIndex } = getNewTemplateSliceIndices(
        loadingGalleryIndex,
        isMobileView,
      );
      const galleryTemplates = templates.slice(startIndex, endIndex);

      if (!galleryTemplates.length) return galleries;

      return [
        ...galleries.slice(0, -1),
        <Box
          key={`gallery-${startIndex}`}
          paddingTop={isMobileView ? '24px' : '36px'}
          ref={(el) => el && focusFirstTemplateCard(el)}
        >
          <RenderGallery
            templates={galleryTemplates}
            isMobileView={isMobileView}
            logTemplateClick={logTemplateClick}
          />
        </Box>,
      ];
    },
    [templates, isMobileView, logTemplateClick],
  );

  const shouldFetchMoreTemplates = (): boolean => {
    return (
      visibleCount >= templates.length && templates.length < totalAvailable
    );
  };

  const createLoadingGallery = useCallback((): React.ReactNode => {
    return (
      <Box key="loading-gallery" paddingTop={isMobileView ? '30px' : '24px'}>
        <LoadingTemplateGallery isMobileView={isMobileView} />
      </Box>
    );
  }, [isMobileView]);

  useEffect(() => {
    setRenderedGalleries((prevGalleries) =>
      replaceLoadingGalleryWithContent(prevGalleries),
    );
  }, [replaceLoadingGalleryWithContent]);

  const initialTemplates = useMemo(
    () => templates.slice(0, initialTemplateCount),
    [templates, isMobileView],
  );

  const InitialGallery = useMemo(() => {
    return (
      <RenderGallery
        shouldShowThreeInRow={
          initialTemplates.length <= MAX_TEMPLATES_COUNT_FOR_THREE_IN_ROW
        }
        templates={initialTemplates}
        isMobileView={isMobileView}
        logTemplateClick={logTemplateClick}
      />
    );
  }, [initialTemplates, isMobileView]);

  const iconConfig = useMemo(
    () => ({
      element: <ArrowSvg />,
      baseClassName: s.showMoreLessButtonIcon,
      moreClassName: s.isMore,
      lessClassName: s.isLess,
    }),
    [],
  );

  const handleToggle = useCallback(
    (shouldShowMore: boolean) => {
      if (shouldShowMore) {
        const expectedNextCount = calculateNextCount(
          visibleCount,
          totalAvailable,
          isMobileView,
        );

        if (shouldFetchMoreTemplates()) {
          onNeedMoreTemplates();
        }

        setRenderedGalleries((prev) => [...prev, createLoadingGallery()]);
        setVisibleCount(expectedNextCount);
      } else {
        setVisibleCount(initialTemplateCount);
        setRenderedGalleries([]);
        onShowLess?.();
        // Keep focus in templates: move to first card under templates root
        focusFirstTemplateCard(templatesRef.current);
      }
    },
    [
      visibleCount,
      totalAvailable,
      shouldFetchMoreTemplates,
      onNeedMoreTemplates,
      createLoadingGallery,
      onVisibleCountChange,
      onShowLess,
    ],
  );

  const effectiveTotalCount = Math.max(templates.length, totalAvailable);

  return (
    <Box
      dataHook={
        isMobileView
          ? 'profile-templates-gallery-mobile'
          : 'profile-templates-gallery'
      }
      direction="vertical"
      marginTop={isMobileView ? '0' : '24px'}
    >
      {InitialGallery}
      {renderedGalleries}
      <CollapseButton
        totalCount={effectiveTotalCount}
        visibleCount={visibleCount}
        initialCount={initialTemplateCount}
        showMoreText={t('show-more-text-button')}
        showLessText={t('show-less-text-button')}
        onToggle={handleToggle}
        iconConfig={iconConfig}
      />
    </Box>
  );
};

export default ProfileTemplateGalleries;
